/**
 * KI-Übersetzung TP1 — Badge (Herkunft/Status) und Popover mit allen
 * Sprachen; Korrektur, "Behalten" (stale) und "Erneut versuchen" (failed)
 * mit translations.write.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui';
import { getThemeColors } from '../../lib/theme';
import { usePermissions } from '../../contexts/PermissionsContext';
import {
  contentTranslationsApi, LANGS, type FieldTranslations, type Lang, type ResolvedTranslation,
} from '../../lib/contentTranslations';
import type { TranslationTarget } from './TranslatedText';

/** Badge je Zustand; im Zustand „original"/„none" ohne KI-Hinweis (Button bleibt für das Popover). */
export function TranslationBadge({ dark, resolved, onClick }: {
  dark: boolean; resolved: ResolvedTranslation; onClick: () => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const src = (resolved.sourceLanguage ?? '').toUpperCase();
  const label = (() => {
    switch (resolved.state) {
      case 'translated': return resolved.entry?.origin === 'manual' ? tr('translation.badge.manual') : tr('translation.badge.machine', { lang: src });
      case 'stale': return tr('translation.badge.stale');
      case 'pending': return tr('translation.badge.pending');
      case 'failed': return tr('translation.badge.failed');
      default: return null;
    }
  })();
  if (!label && resolved.state === 'none') return null;
  const tone = resolved.state === 'failed' || resolved.state === 'stale' ? '#b54708' : t.muted;
  return (
    <button type="button" onClick={onClick} aria-label={label ?? tr('translation.popover.title')} style={{
      border: `1px solid ${t.borderSoft}`, background: t.cardMuted, color: tone, borderRadius: 999,
      padding: '1px 8px', fontSize: 11, cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
    }}>
      {label ?? '🌐'}
    </button>
  );
}

export function TranslationPopover({ dark, original, entries, target, onClose, onChange }: {
  dark: boolean;
  original: string;
  entries: FieldTranslations | undefined;
  target: TranslationTarget;
  onClose: () => void;
  onChange: (next: FieldTranslations) => void;
}) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('translations.write');
  const [editing, setEditing] = useState<Lang | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Gibt zurück, ob die Aktion erfolgreich war (für den Save-Button: Popover
  // danach schließen, damit die Korrektur nicht doppelt — innen und in der
  // Lese-Anzeige darüber — sichtbar bleibt).
  const run = async (fn: () => Promise<FieldTranslations | void>): Promise<boolean> => {
    setBusy(true); setError(null);
    try {
      const next = await fn();
      if (next) onChange(next);
      setEditing(null);
      return true;
    } catch {
      setError(tr('translation.popover.error'));
      return false;
    } finally {
      setBusy(false);
    }
  };

  // Neu übersetzen / erneut versuchen: Das Backend setzt die betroffenen Zeilen auf "pending" —
  // lokal genauso, damit der Badge sofort „Übersetzung läuft …" zeigt und die Aktion verschwindet
  // (statt durch weitere Klicks weitere kostenpflichtige Jobs anzustoßen).
  // `onlyLang`: nur diese (veraltete, wieder maschinell werdende) Sprache; sonst alle nicht
  // manuellen Einträge des Felds.
  const retranslate = (onlyLang?: Lang) => run(async () => {
    const { queued } = await contentTranslationsApi.retranslate(
      target.type, target.id, [target.field], onlyLang ? [onlyLang] : undefined,
    );
    if (!queued || !entries) return;
    const next: FieldTranslations = { ...entries };
    for (const l of LANGS) {
      const cur = next[l];
      if (!cur) continue;
      if (onlyLang ? l === onlyLang : cur.origin !== 'manual') {
        next[l] = { ...cur, status: 'pending', ...(onlyLang ? { origin: 'machine' as const } : {}) };
      }
    }
    return next;
  });

  return (
    <div role="dialog" aria-label={tr('translation.popover.title')} style={{
      position: 'absolute', zIndex: 30, top: '100%', left: 0, marginTop: 6, width: 'min(520px, 90vw)',
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 12, padding: 14,
      boxShadow: '0 12px 32px rgba(16,24,40,0.18)',
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: t.muted, textTransform: 'uppercase', marginBottom: 4 }}>
        {tr('translation.popover.original')}
      </div>
      <div style={{ fontSize: 13, color: t.text, whiteSpace: 'pre-wrap', marginBottom: 12 }}>{original || '—'}</div>

      {LANGS.map(lang => {
        const e = entries?.[lang];
        const L = lang.toUpperCase();
        return (
          <div key={lang} style={{ borderTop: `1px solid ${t.borderSoft}`, padding: '8px 0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: t.muted }}>
              <strong style={{ color: t.heading }}>{L}</strong>
              {e && <span>{tr(`translation.origin.${e.origin}`)}</span>}
              {e?.status === 'stale' && <span style={{ color: '#b54708' }}>{tr('translation.popover.stale_hint')}</span>}
              {canWrite && e?.origin !== 'source' && e?.origin !== 'extracted' && editing !== lang && (
                <button type="button" aria-label={tr('translation.popover.edit', { lang: L })} disabled={busy}
                  onClick={() => { setEditing(lang); setDraft(e?.text ?? ''); }}
                  style={{ marginLeft: 'auto', border: 'none', background: 'none', cursor: 'pointer', color: t.muted }}>✎</button>
              )}
            </div>
            {editing === lang ? (
              <div style={{ marginTop: 6 }}>
                <textarea aria-label={tr('translation.popover.field', { lang: L })} value={draft}
                  onChange={ev => setDraft(ev.target.value)} rows={3}
                  style={{ width: '100%', boxSizing: 'border-box', fontFamily: 'Outfit,sans-serif', fontSize: 13 }} />
                <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                  <Button size="sm" dark={dark} disabled={busy || !draft.trim()}
                    onClick={() => {
                      void run(() => contentTranslationsApi.update(target.type, target.id, target.field, lang, draft))
                        .then(ok => { if (ok) onClose(); });
                    }}>
                    {tr('translation.popover.save')}
                  </Button>
                  <Button size="sm" variant="secondary" dark={dark} onClick={() => setEditing(null)}>{tr('translation.popover.cancel')}</Button>
                </div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: t.text, whiteSpace: 'pre-wrap', marginTop: 2 }}>{e?.text ?? '—'}</div>
            )}
            {canWrite && e?.status === 'stale' && editing !== lang && (
              <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                <Button size="sm" variant="secondary" dark={dark} disabled={busy}
                  onClick={() => { void retranslate(lang); }}>
                  {tr('translation.popover.retranslate')}
                </Button>
                <Button size="sm" variant="secondary" dark={dark} disabled={busy}
                  onClick={() => run(() => contentTranslationsApi.keep(target.type, target.id, target.field, lang))}>
                  {tr('translation.popover.keep')}
                </Button>
              </div>
            )}
            {canWrite && e?.status === 'failed' && (
              <Button size="sm" variant="secondary" dark={dark} disabled={busy}
                onClick={() => { void retranslate(); }}>
                {tr('translation.popover.retry')}
              </Button>
            )}
          </div>
        );
      })}
      {error && <div style={{ color: '#b42318', fontSize: 12 }}>{error}</div>}
      <div style={{ textAlign: 'right', marginTop: 8 }}>
        <Button size="sm" variant="ghost" dark={dark} onClick={onClose}>{tr('translation.popover.close')}</Button>
      </div>
    </div>
  );
}
